import { StripeElementLocale } from '@stripe/stripe-js';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { IPaymentOption } from '../../../../../types/@types/generated/contentful';
import { ImpacterResponse } from '../../../../../types/ImpacterTypes';
import { microcopySet, useMicrocopy } from '../../../../../utils/contentfulMicrocopy';
import { scrollToDonationBox } from '../../../../../utils/helpers/scroll';
import useLocale from '../../../../../utils/hooks/useLocale';
import { DONATION_BOX_ID } from '../../../payment/constants';
import FixedMobileButton from '../../../payment/FixedMobileButton';
import { StripePaymentCategory } from '../../../payment/PaymentCategory';
import StripeCheckoutWithSelector from '../../../payment/StripeCheckoutWithSelector';

function StripeInlineWidget({
  impacters,
  category,
  campaign,
  paymentOption,
  thankYouPageSlugOverride,
  colorTheme,
  variant,
  noOuterBorder,
  disableFixedMobileButton,
}: {
  impacters?: Pick<ImpacterResponse, 'id' | 'externalId' | 'nickname'>[];
  category?: StripePaymentCategory;
  campaign?: string;
  paymentOption?: IPaymentOption;
  thankYouPageSlugOverride?: string;
  variant?: 'with-borders' | 'without-borders';
  colorTheme?: 'default' | 'dark' | 'ppc';
  noOuterBorder?: boolean;
  disableFixedMobileButton?: boolean;
}) {
  const router = useRouter();
  const locale = useLocale();
  const t = useMicrocopy();
  const [showDonationButton, setShowDonationButton] = useState(false);
  const [isClientSideRender, setIsClientSideRender] = useState(false);

  const onSuccess = ({
    paymentRequestId,
    subscriptionId,
  }: {
    paymentRequestId?: string;
    subscriptionId?: string;
  }) => {
    const id = subscriptionId || paymentRequestId;
    const type = paymentRequestId ? 'one-off' : 'subscription';
    const count = impacters?.length ?? 1;
    if (thankYouPageSlugOverride) {
      router.push(
        `/thank-you?type=${type}&id=${id}&count=${count}&campaign=${campaign}&slug=${thankYouPageSlugOverride}`,
        `/thank-you?type=${type}&id=${id}&count=${count}&campaign=${campaign}&slug=${thankYouPageSlugOverride}`,
        { locale },
      );
    } else {
      router.push(
        `/thank-you?type=${type}&id=${id}&count=${count}&campaign=${campaign}`,
        `/thank-you?type=${type}&id=${id}&count=${count}&campaign=${campaign}`,
        { locale },
      );
    }
  };

  const handleScroll = useCallback(() => {
    const donationBox = document.getElementById(DONATION_BOX_ID);
    if (donationBox) {
      const donationBoxTopPos = donationBox.getBoundingClientRect().bottom + window.scrollY;
      if (window.scrollY > donationBoxTopPos) {
        setShowDonationButton(true);
      } else {
        setShowDonationButton(false);
      }
    }
  }, [setShowDonationButton]);

  useEffect(() => {
    const debouncedHandleScroll = debounce(handleScroll, 300, {
      leading: true,
      trailing: true,
      maxWait: 100,
    });
    window.addEventListener('scroll', debouncedHandleScroll);
    return () => window.removeEventListener('scroll', debouncedHandleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsClientSideRender(true);
  }, [setIsClientSideRender]);

  return (
    <>
      <StripeCheckoutWithSelector
        id={DONATION_BOX_ID}
        impacters={impacters}
        category={category}
        onSuccess={onSuccess}
        campaign={campaign}
        paymentOption={paymentOption?.fields}
        locale={locale as StripeElementLocale}
        variant={variant}
        colorTheme={colorTheme}
        noOuterBorder={noOuterBorder}
      />
      {!disableFixedMobileButton && isClientSideRender && (
        <FixedMobileButton
          show={showDonationButton}
          onDonate={scrollToDonationBox}
          donateText={t(microcopySet.PAYMENT_OPTION, 'donate')}
        />
      )}
    </>
  );
}

export default StripeInlineWidget;
